<template>
  <div class="history-item">
    <div class="date">
      {{ dispDate }}
    </div>
    <div class="text" v-html="dispText" />
    <slot />
    <div v-if="displayValues" class="modified-values">
      <div class="older-wrapper">
        <EditableLabel
          :value="dispFrom"
          class="older"
          disabled
          :use-tiptap-editor="item.field === 'action.comment'"
        />
      </div>
      <EditableLabel
        :value="dispTo"
        class="newer"
        disabled
        :use-tiptap-editor="item.field === 'action.comment'"
      />
    </div>
    <div class="bottom-sep" />
  </div>
</template>

<script>
/**
 * Item in actions or problems history.
 * Related to a field edition or a global action.
 * Possible items :
 *   - creation
 *   - field edition (eg: Team A edited field 'Who' from ... to ...
 *   - data creation (eg: Team B added a cause to the problem)
 *   - data edition
 *   - data deletion
 *   - send to other team (eg: Team A shared the problem with Teams B and C)
 *   - archive
 *
 * TODO: Manage this data through Vuex, remove getReportingData, Remove all props
 * @vue-prop {Array} [allTypes=[]] - PB types
 * @vue-prop {Array} [allZones=[]] - Company zones
 * @vue-prop {Array} [allIndicators=[]] - Problem's owner team indicators
 * @vue-prop {Array} [allKeywords=[]] - All available keywords
 * @vue-prop {Array} [allUsers=[]] - All users
 * @vue-prop {Array} [allCauses=[]] - All causes related to this problem
 * @vue-prop {String} [translationSuffix=PB] - Generic suffix to use for the translation key
 */

import { mapState } from 'vuex';
import { FETCH_TEAMS } from '@/store/action-types';
import {
  GET_ZONES,
  GET_USERS,
  GET_PB_TYPES,
  GET_KEYWORDS,
  GET_TEAMS,
} from '@/store/mutation-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import EllipsisDiv from '@/components/ellipsize.vue';
// Utils
import { nestedFindItemWithId } from '@/utils/main';
import HistoryChangeType from '@/utils/classes/HistoryChangeType';

const historyItemPrefixes = {
  [HistoryChangeType.creation]: 'HISTORY_CREATION_',
  [HistoryChangeType.edition]: 'HISTORY_EDIT_',
  [HistoryChangeType.deletion]: 'HISTORY_DELETION_',
  [HistoryChangeType.childCreation]: 'HISTORY_CREATION_',
  [HistoryChangeType.childEdition]: 'HISTORY_EDIT_',
  [HistoryChangeType.childDeletion]: 'HISTORY_DELETION_',
};

const possibleDataTypes = {
  problem: {
    suffix: 'PB',
    titleKey: 'lWhat',
    array: 'allProblems',
  },
  cause: {
    suffix: 'CAUSE',
    titleKey: 'text',
    array: 'allCauses',
  },
  action: {
    suffix: 'ACTION',
    titleKey: 'name',
    array: 'allActions',
  },
};

export default {

  name: 'HistoryItem',

  components: {
    EditableLabel,
    EllipsisDiv,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    allIndicators: {
      type: Array,
      default: () => [],
    },
    allCauses: {
      type: Array,
      default: () => [],
    },
    allProblems: {
      type: Array,
      default: () => [],
    },
    allActions: {
      type: Array,
      default: () => [],
    },
    allActionPlans: {
      type: Array,
      default: () => [],
    },
    translationSuffix: {
      type: String,
      default: 'PB',
    },
  },

  data() {
    return {
      dispFromKey: 1,
      dispToKey: 1,
      pdcaArray: [
        { id: 0, name: '' },
        { id: 1, name: this.$t('message.PDCA_PLAN') },
        { id: 2, name: this.$t('message.PDCA_DO') },
        { id: 3, name: this.$t('message.PDCA_CHECK') },
        { id: 4, name: this.$t('message.PDCA_ACT') },
      ],
      securingActionStates: [
        { id: 0, name: this.$t('message.ACTION_STATE_PENDING') },
        { id: 1, name: this.$t('message.ACTION_STATE_ONGOING') },
        { id: 2, name: this.$t('message.ACTION_STATE_COMPLETED') },
      ],
    };
  },

  computed: {
    /** Displayed date of the history item */
    dispDate() {
      return this.$moment(this.item.date).format(this.$t('fullDateTimeFormat'));
    },
    /** Text displayed describing what changed */
    dispText() {
      // Concatenation of 'the team ... did what'
      if (this.user) {
        const { firstName, lastName } = this.user;
        return this.$t('message.HISTORY_THE_TEAM_AND_USER', {
          team: this.team ? this.team.name : '',
          user: `${firstName} ${lastName}`,
          didWhat: this.dispChangedWhat,
        });
      }
      return this.$t('message.HISTORY_THE_TEAM', {
        team: this.team ? this.team.name : '',
        didWhat: this.dispChangedWhat,
      });
    },
    dispChangedWhat() {
      let { type, trSuffix, field, childRef } = this.item;
      trSuffix = trSuffix || this.translationSuffix;
      if ([HistoryChangeType.edition, HistoryChangeType.childEdition].includes(type)) {
        const trField = this.$t(`message.HISTORY_FIELD_${trSuffix}_${field.toUpperCase()}`);
        return this.$t(`message.${historyItemPrefixes[type]}${trSuffix}`, { field: trField });
      }
      if ([HistoryChangeType.childCreation, HistoryChangeType.childDeletion].includes(type)) {
        const childRefs = childRef.split(':');
        return this.$t(
          `message.${historyItemPrefixes[type]}${this.childDataTypeDetails.suffix}`,
          { title: this.getChildDataTitle() },
        );
      }
      return this.$t(`message.${historyItemPrefixes[type]}${this.dataTypeDetails.suffix}`, { title: this.getChildDataTitle() });
    },
    /** First part of dispText describing which team made the change */
    dispTeam() {
      // TODO: Handle null team (ie: team was deleted after it made the change)
      return this.$t('message.HISTORY_THE_TEAM', { team: this.team ? this.team.name : '' });
    },
    /** Wether values should be displayed or not */
    displayValues() {
      const { childEdition, childDeletion, edition } = HistoryChangeType;
      // Checking if the field is related to a cause verification to hide the From and To values,
      // otherwise it will display 0 > 1
      if (
        this.item.field === 'cause.isVerified'
        || this.item.field === 'cause.hideInSheet'
        || this.item.field === 'cause.isReal'
      ) {
        return false;
      }
      return [childEdition, childDeletion, edition].includes(this.item.type) && this.dispFrom !== this.dispTo;
    },
    /** Former value of the edited field */
    dispFrom() {
      const val = this.getValuesFromArray(this.item.fromValue);
      if (!val && this.originalField === 'deadline') {
        return this.$moment(this.item.fromValue).format(this.$t('dateFormat'));
      }
      return typeof val === 'string' ? val : this.item.fromValue;
    },
    /** New value of the edited field */
    dispTo() {
      if (this.item.type === HistoryChangeType.childDeletion) {
        return '';
      }
      const val = this.getValuesFromArray(this.item.toValue);
      if (!val && this.originalField === 'deadline') {
        return this.$moment(this.item.toValue).format(this.$t('dateFormat'));
      }
      return typeof val === 'string' ? val : this.item.toValue;
    },
    /** Team that made the change */
    team() {
      return this.teams.find(t => t.id === this.item.team);
    },
    /** User that made the change */
    user() {
      return this.allUsers.find(u => u.id === this.item.user);
    },
    /** Array: Users extracted from teams */
    allUsers() {
      return this.teams.reduce((all, team) => [...all, ...team.users], []);
    },
    /** Original field as inserted in the DB */
    originalField() {
      const { field } = this.item;
      const fieldComps = field.split('.');
      return fieldComps.length > 1 ? fieldComps[fieldComps.length - 1] : field;
    },
    /** Utils variables of the current data type */
    dataTypeDetails() {
      let details = null;
      Object.keys(possibleDataTypes).forEach((type) => {
        if (this.item[type] !== null && !isNaN(this.item[type])) {
          details = possibleDataTypes[type];
        }
      });
      return details;
    },
    /** Utils variables of the child data type */
    childDataTypeDetails() {
      const { childRef } = this.item;
      const refs = childRef.split(':');
      const childType = refs[0];
      let details = null;
      Object.keys(possibleDataTypes).forEach((type) => {
        if (type === childType && refs[1] && !isNaN(refs[1])) {
          details = possibleDataTypes[type];
        }
      });
      return details;
    },
    ...mapState({
      teams: state => state[GET_TEAMS.stateKey] || [],
      teamsLoading: state => state[GET_TEAMS.loadingKey],
      allZones: state => state[GET_ZONES.stateKey] || [],
      allZonesLoading: state => state[GET_ZONES.loadingKey],
      allTypes: state => state[GET_PB_TYPES.stateKey] || [],
      allTypesLoading: state => state[GET_PB_TYPES.loadingKey],
      allKeywords: state => state[GET_KEYWORDS.stateKey] || [],
      allKeywordsLoading: state => state[GET_KEYWORDS.loadingKey],
    }),
  },

  watch: {
    dispFrom() {
      this.dispFromKey++;
    },
    dispTo() {
      this.dispToKey++;
    },
  },

  created() {
    // Refresh teams
    if (!this.teamsLoading) {
      this.$store.dispatch(FETCH_TEAMS);
    }
  },

  methods: {
    getValuesFromArray(ids) {
      let array;
      let key = 'name';
      let childrenKey = 'zones';
      if (this.originalField === 'indicators') {
        array = this.allIndicators;
      } else if (this.originalField === 'leaders') {
        array = this.allUsers;
        key = 'label';
      } else if (this.originalField === 'zones') {
        array = this.allZones;
        childrenKey = 'zones';
      } else if (this.originalField === 'types') {
        array = this.allTypes;
        childrenKey = 'types';
      } else if (this.originalField === 'keywords') {
        array = this.allKeywords;
        key = 'word';
      } else if (this.originalField === 'cause') {
        array = this.allCauses;
        key = 'text';
      } else if (this.originalField === 'plan') {
        array = this.allActionPlans;
      } else if (this.originalField === 'pdca') {
        array = this.pdcaArray;
      } else if (this.originalField === 'state') {
        array = this.securingActionStates;
      }

      if (array) {
        if (ids === '' || ids === null) {
          return '';
        }
        const items = ids.split(',').map(id => Number(id));
        return items.map((id) => {
          const item = nestedFindItemWithId(array, id, childrenKey);
          return item === null && id !== 0 ? this.$t('message.DELETED_PLACEHOLDER') : item && item[key] || '';
        }).join(', ');
      }
      return false;
    },
    /**
     * Retrieve the title of the current linked data, depending of the type of data the history change refers to
     */
    getChildDataTitle() {
      const refs = this.item.childRef.split(':');
      const childId = Number(refs[1]);
      const { array, titleKey } = this.childDataTypeDetails;
      const relatedItem = (this[array] || []).find(c => c.id === childId);
      return relatedItem ? relatedItem[titleKey] : '';
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.history-item {

  .date {
    color: $color-light-blue-text;
  }
  .modified-values {
    $hc-line-height: 1.3em;
    margin-top: 5px;
    font-style: italic;
    font-size: 0.9em;
    line-height: $hc-line-height;

    > *, .older {
      padding: 1px 3px;
      min-height: 1.3em;
      display: inline-block;
      max-width: calc(50% - 4px);
      max-height: #{$hc-line-height * 3.2};
      vertical-align: middle;
      box-sizing: border-box;

      &.older-wrapper {
        position: relative;
        background: #c3c2c2;

        &::after {
          top: 50%;
          left: 100%;
          border: solid transparent;
          content: " ";
          height: 0;
          width: 0;
          position: absolute;
          border-left-color: #c3c2c2;
          border-width: 8px;
          margin-top: -8px;
        }
        .older {
          max-width: 100%;
        }
      }
      &.newer {
        background: #a7c9ee;
        margin-left: 8px;
      }

      &.editable-label, .editable-label {
        overflow: hidden;

        .text-field {
          margin: 0;

          &__field {
            border: none;
          }
        }
      }
    }
  }
  .bottom-sep {
    width: 100%;
    height: 1px;
    background: #888888;
    margin: 8px 0px;
  }
}
</style>
